// ---------------------------------------------------------------------------
// AcgPicker — Search AAD groups and register them as Access Control Groups.
//
// Flow:
//   1. User types a group name → debounced GET /accesscontrolgroup/aad/search
//   2. User selects from dropdown → AAD group ID shown below the input
//   3. User clicks "+ Add" → POST /accesscontrolgroup → onAdd(acg)
// ---------------------------------------------------------------------------

import { Button, Combobox, Option, Spinner } from '@fluentui/react-components';
import { Add16Regular, Search16Regular } from '@fluentui/react-icons';
import { useCallback, useMemo, useState } from 'react';

import {
  ACG_PICKER_ADD_BUTTON,
  ACG_PICKER_GROUP_ID_HINT,
  ACG_PICKER_GROUP_LABEL,
  ACG_PICKER_SEARCH_PLACEHOLDER,
  ACG_PROVIDER_AAD,
} from '../../../constants/index.js';
import { useAadGroupSearch } from '../../../hooks/useAadGroupSearch/useAadGroupSearch.js';
import type { AadGroupInfo } from '../../../services/accessControlService.interface.js';

import type { AcgPickerProps } from './AcgPicker.interface.js';
import { useAcgPickerStyles } from './AcgPicker.style.js';

export function AcgPicker({ service, onAdd }: AcgPickerProps) {
  const styles = useAcgPickerStyles();

  const [searchQuery, setSearchQuery] = useState('');
  const [selectedGroup, setSelectedGroup] = useState<AadGroupInfo | null>(null);
  const [isAdding, setIsAdding] = useState(false);
  const [addError, setAddError] = useState<string | null>(null);

  const { groups, isLoading: isSearching } = useAadGroupSearch({
    query: searchQuery,
    service,
  });

  // Build a lookup map so we can resolve displayName → AadGroupInfo on option select.
  const groupByDisplayName = useMemo(
    () => new Map(groups.map((g) => [g.displayName, g])),
    [groups],
  );

  const handleOptionSelect = useCallback(
    (_: unknown, data: { optionValue?: string }) => {
      const group = data.optionValue
        ? groupByDisplayName.get(data.optionValue)
        : undefined;
      setSelectedGroup(group ?? null);
      setAddError(null);
    },
    [groupByDisplayName],
  );

  const handleAdd = useCallback(async () => {
    if (!selectedGroup) return;

    setIsAdding(true);
    setAddError(null);

    try {
      const acg = await service.createAccessControlGroup({
        sgGroupId: selectedGroup.groupId,
        provider: ACG_PROVIDER_AAD,
        displayName: selectedGroup.displayName,
      });
      onAdd(acg);
      setSearchQuery('');
      setSelectedGroup(null);
    } catch (err) {
      setAddError(err instanceof Error ? err.message : 'Failed to add group.');
    } finally {
      setIsAdding(false);
    }
  }, [selectedGroup, service, onAdd]);

  const groupIdLabel = selectedGroup?.groupId ?? ACG_PICKER_GROUP_ID_HINT;

  return (
    <div>
      <div className={styles.pickerCard}>
        {/* Left label */}
        <div className={styles.labelSide}>
          <Search16Regular aria-hidden="true" />
          <span className={styles.labelText}>{ACG_PICKER_GROUP_LABEL}</span>
        </div>

        {/* Search input + hint */}
        <div className={styles.inputSide}>
          <Combobox
            className={styles.combobox}
            placeholder={ACG_PICKER_SEARCH_PLACEHOLDER}
            value={searchQuery}
            onChange={(e) => {
              setSearchQuery(e.target.value);
              setSelectedGroup(null);
            }}
            onOptionSelect={handleOptionSelect}
            disabled={isAdding}
            aria-label={ACG_PICKER_GROUP_LABEL}
            clearable={false}
            expandIcon={null}
            positioning={{
              position: 'below',
              align: 'start',
              fallbackPositions: ['above'],
              // Clear Combobox's default input-width matching.
              matchTargetSize: undefined,
              autoSize: false,
            }}
            listbox={{ className: styles.listbox }}
          >
            {isSearching ? (
              <Option key="loading" value="" disabled>
                Searching...
              </Option>
            ) : groups.length === 0 && searchQuery.trim().length > 0 ? (
              <Option key="empty" value="" disabled>
                No groups found
              </Option>
            ) : (
              groups.map((g) => (
                <Option key={g.groupId} value={g.displayName}>
                  {g.displayName}
                </Option>
              ))
            )}
          </Combobox>
          <span className={styles.groupIdHint} aria-live="polite">
            {groupIdLabel}
          </span>
        </div>

        {/* Add button */}
        <Button
          className={styles.addButton}
          appearance="secondary"
          icon={isAdding ? <Spinner size="extra-tiny" /> : <Add16Regular />}
          disabled={!selectedGroup || isAdding}
          onClick={() => {
            void handleAdd();
          }}
          aria-label={ACG_PICKER_ADD_BUTTON}
        >
          {ACG_PICKER_ADD_BUTTON}
        </Button>
      </div>

      {addError !== null && (
        <span className={styles.errorText} role="alert">
          {addError}
        </span>
      )}
    </div>
  );
}
